import { FC, useMemo } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import { currencies } from '@const/currency';
import { EIntentStatusTranslate, ETransactionStatusTranslate } from '@locales/index';
import { useTranslation } from 'react-i18next';
import { Icon } from '@components/icons';
import { ETransactionStatus, TTransaction, TTransactionLite } from '@xeppt/xeppt-sdk/types';
import {
    formatDate,
    getFundsOperation,
    getIntentStatus,
    getTransactionStatus,
    isIntentPayment,
    prettifyAmount
} from '@utils/index';
import { getInternalTransferAction } from '@utils/payment';
import Status from '@components/common/status';
import { ETransactionAction, ETransactionType } from '@xeppt/xeppt-sdk/types/transaction';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import { apiTransactionsService } from '@api';
import { useLocation, useNavigate } from 'react-router-dom';
import { EFundsOperation } from '@enum';
import { TPaymentIntent } from '@xeppt/xeppt-sdk/types/payment-intent';
import { useApiMutation } from '@hooks/api/useApiMutation';

export interface IProps {
    transaction: TTransactionLite | TPaymentIntent;
    transactionProvided?: boolean;
    isCard?: boolean;
    isSale?: boolean;
    refetchTransactions?: () => void;
    // Optional override for plain-transaction row clicks — used by the
    // dev-portal Payments page to open its own merchant drawer instead of the
    // customer-facing VIEW_TRANSACTION modal. Every other consumer of this
    // component leaves it unset, so their behavior is unchanged.
    onTransactionClick?: (transaction: TTransaction) => void;
}

const cx = classNames.bind(styles);
const TransactionItem: FC<IProps> = ({ transaction, refetchTransactions, isCard, isSale, onTransactionClick }) => {
    const { t, i18n } = useTranslation('components', {
        keyPrefix: 'transaction_tables.transaction_item'
    });
    const location = useLocation();
    const navigate = useNavigate();
    const { changeModalData, onOpen } = useModalContext();

    const isMoneyOutTransaction = useMemo(
        () => transaction.type === ETransactionType.CREDIT,
        [transaction]
    );

    // Gates only the buyer's "Purchase" label (the CREDIT payment leg in
    // getTransactionDescription below) — the merchant's incoming DEBIT leg is always the
    // real money-movement type and never consults this. Off the merchant Sales tab
    // (customer view / merchant Transactions tab) a PAYMENT action has always meant a
    // real purchase, so it's unconditional there. On the Sales tab (isSale) a founder
    // screenshot showed a non-order, P2P-shaped row also getting the blind label, so
    // there we require positive order-linkage evidence: the merchant-leg flag the drawer
    // reads (details.is_merchant_view) or the order linkage behind its "View order" link
    // (details.order_id / order_reference_code). Without it the label falls back to the
    // real funds-operation wording.
    const isGenuineStoreLeg = useMemo(() => {
        if (isIntentPayment(transaction)) return false;
        if (!isSale) return true;
        const details = (transaction as any).details;
        return Boolean(
            details?.is_merchant_view === true ||
                details?.order_id ||
                details?.order_reference_code
        );
    }, [transaction, isSale]);

    const amountStyles = useMemo(
        () =>
            cx([
                styles.amount,
                {
                    plus: !isIntentPayment(transaction) ? !isMoneyOutTransaction : false,
                    isCancel:
                        transaction.status === ETransactionStatus.CANCELED ||
                        transaction.status === ETransactionStatus.DECLINED
                }
            ]),
        [transaction]
    );

    const iconStyles = useMemo(
        () =>
            cx([
                styles.icon,
                {
                    isWallet: !isMoneyOutTransaction
                }
            ]),
        [transaction]
    );

    const getTransactionDescription = useMemo(() => {
        if (isIntentPayment(transaction)) {
            return transaction?.type === ETransactionType.CREDIT
                ? 'Send money via XEPPT'
                : 'Send request via XEPPT';
        } else {
            if (transaction?.action === ETransactionAction.REFUND) {
                return `Refund`;
            }
            const operation = getInternalTransferAction(
                transaction.method,
                transaction.action,
                transaction.type
            );
            switch (operation) {
                case EFundsOperation.WALLET_CREDIT_TRANSFER:
                    return `Send money via XEPPT`;
                case EFundsOperation.WALLET_DEBIT_TRANSFER:
                    return `Receive money via XEPPT`;
                case EFundsOperation.WALLET_DEBIT_REQUEST:
                    return `Send request via XEPPT`;
                case EFundsOperation.WALLET_CREDIT_REQUEST:
                    return `Receive request via XEPPT`;
                case EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT:
                    return `Load XEPPT card balance`;
                case EFundsOperation.WALLET_CREDIT_CARD_SERVICE:
                    return `XEPPT card order`;
                case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                    return `Send money via e-Transfer`;
                case EFundsOperation.E_TRANSFER_DEBIT_TRANSFER:
                    return `Receive money via e-Transfer`;
                case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                    return `Send request via e-Transfer`;
                case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                    return `Pay bill`;
                case EFundsOperation.EFT_DEBIT_DEPOSIT:
                    return `Load balance from bank account`;
                case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                    return `Withdraw to bank account`;
                case EFundsOperation.EPS_DEBIT_DEPOSIT:
                    return `Load balance from linked card`;
                case EFundsOperation.CARD_CREDIT_WITHDRAWAL:
                    return `Payment with card`;
                case EFundsOperation.CARD_DEBIT_DEPOSIT:
                    return `Load balance`;
                case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                    return transaction.destination.includes(' + ')
                        ? 'Bulk XEPPT gift card issuance'
                        : 'XEPPT gift card issuance';
                case EFundsOperation.GIFT_CARD_CREDIT_RESERVE:
                    return transaction.destination.includes(' + ')
                        ? 'Bulk XEPPT gift card funds reservation'
                        : 'XEPPT gift card funds reservation';
                case EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM:
                    return 'XEPPT gift card redemption';
                case EFundsOperation.GIFT_CARD_DEBIT_RESERVE:
                    return 'Release reserved funds';
                case EFundsOperation.WALLET_DEBIT_PAYMENT:
                    // The merchant's incoming leg always shows the real money-movement
                    // type — never "Purchase" (founder: the merchant view must show the
                    // real transaction type, e.g. "Receive money via XEPPT").
                    return `Receive money via XEPPT`;
                case EFundsOperation.WALLET_CREDIT_PAYMENT:
                    // The buyer's own outgoing leg is a "Purchase" on their view; a
                    // PAYMENT-shaped row without store-order context (isGenuineStoreLeg)
                    // falls back to the transfer wording.
                    return isGenuineStoreLeg ? t('purchase') : `Send money via XEPPT`;
            }
        }
    }, [t, transaction, isGenuineStoreLeg]);

    const getTransactionTitle = useMemo(() => {
        if (isIntentPayment(transaction))
            return `${transaction.contact_first_name} ${transaction.contact_last_name} (${transaction.contact_email})`;
        // A refund leg is CreditType, but the refunded CUSTOMER's name is always
        // in `source` (destination is the merchant's own wallet) — same early
        // action-based branch as getTransactionDescription just above.
        if (transaction?.action === ETransactionAction.REFUND) {
            return transaction?.source;
        }
        return !isMoneyOutTransaction ? transaction?.source : transaction?.destination;
    }, [transaction]);

    const { handleRequest: fetchTransaction } = useApiMutation({
        method: (id?: string) => apiTransactionsService.getTransactionById(id || ''),
        onSuccess: (data) => {
            handleViewTransaction(data);
        }
    });

    const { handleRequest: fetchPaymentIntent } = useApiMutation({
        method: (id?: string) => apiTransactionsService.getPaymentIntentById(id || ''),
        onSuccess: (data) => {
            handleViewPaymentIntent(data);
        }
    });

    const handleViewTransaction = (transaction: TTransaction) => {
        if (onTransactionClick) {
            onTransactionClick(transaction);
            return;
        }
        changeModalData({ transaction, refetchTransactions });
        onOpen({
            modalId: modalIds.VIEW_TRANSACTION,
            onClose: () => navigate(location.pathname)
        });
    };

    const handleViewPaymentIntent = (transaction: TPaymentIntent) => {
        changeModalData({ transaction, refetchTransactions });
        onOpen({
            modalId: modalIds.VIEW_EXTERNAL_TRANSACTION,
            onClose: () => navigate(location.pathname)
        });
    };

    const handleViewCardTransaction = () => {
        changeModalData({ transaction, refetchTransactions });
        onOpen({
            modalId: modalIds.VIEW_CARD_TRANSACTION
        });
    };

    const transactionAmount = useMemo(() => {
        // Payment intents are net-keyed and carry no XEPPT fee leg here — show the raw amount.
        if (isIntentPayment(transaction)) {
            return transaction.amount;
        }
        // Mirror view_transaction's getSenderString so the list headline equals the GROSS the
        // initiator transacts and list/detail never disagree. Outgoing (CREDIT) legs add the
        // fee; the two gross-keyed money-request legs — the requester's own DEBIT leg, where
        // they keyed the GROSS charged to the payer — also add the fee so the row matches the
        // detail's "Amount you requested". Every other DEBIT leg (receive/redeem/load) is net.
        const operation = getFundsOperation(transaction);
        const isGrossKeyedRequest =
            operation === EFundsOperation.WALLET_DEBIT_REQUEST ||
            operation === EFundsOperation.E_TRANSFER_DEBIT_REQUEST;
        if (transaction.type === ETransactionType.CREDIT || isGrossKeyedRequest) {
            return transaction.amount + transaction.fee;
        }
        return transaction.amount;
    }, [transaction]);

    const isShowStatus = useMemo(() => {
        if (isIntentPayment(transaction)) {
            return true;
        } else {
            return transaction.status !== ETransactionStatus.APPROVED;
        }
    }, [transaction]);

    return (
        <button
            className={styles.table_item}
            onClick={() => {
                if (isCard) {
                    handleViewCardTransaction();
                } else if (isIntentPayment(transaction)) {
                    fetchPaymentIntent(transaction.id);
                } else {
                    fetchTransaction(transaction.id);
                }
            }}>
            <div className={styles.left}>
                <div className={iconStyles}>
                    {!isIntentPayment(transaction) && transaction?.automatic ? (
                        <Icon
                            className={`${styles.recurring_icon} ${!isMoneyOutTransaction ? styles.rotated : ''}`}
                            name="transaction_arrow_recurring"
                            width={30}
                            height={30}
                        />
                    ) : isMoneyOutTransaction ? (
                        <Icon name="transaction_arrow_up" width={30} height={30} />
                    ) : (
                        <Icon name="transaction_arrow_down" width={30} height={30} />
                    )}
                </div>
                <div className={styles.text_wrapper}>
                    <Typography className={styles.title} variant="body1" weight="semibold">
                        {getTransactionTitle}
                    </Typography>
                    <Typography className={styles.description} variant="body3">
                        <span>{getTransactionDescription}</span>{' '}
                        <span className={styles.dot}>●</span>{' '}
                        <i>{formatDate(transaction.created_at, 'DD MMM YYYY')}</i>
                    </Typography>
                </div>
            </div>
            <div className={styles.right}>
                {isShowStatus && (
                    <Status
                        variant={
                            isIntentPayment(transaction)
                                ? getIntentStatus(transaction.status)
                                : getTransactionStatus(transaction?.status)
                        }
                        className={styles.status}>
                        {transaction?.status && !isIntentPayment(transaction)
                            ? //@ts-ignore
                              ETransactionStatusTranslate[transaction.status]?.[
                                  i18n.language as 'en' | 'fr'
                              ]
                            : EIntentStatusTranslate[transaction.status]?.[
                                  i18n.language as 'en' | 'fr'
                              ]}
                    </Status>
                )}
                <Typography variant="body1" weight="bold" className={amountStyles}>
                    {isMoneyOutTransaction && '-'}
                    {currencies[transaction.currency as 'CAD']?.symbol}
                    {prettifyAmount(transactionAmount.toFixed(2))}
                </Typography>
            </div>
        </button>
    );
};

export default TransactionItem;
